/**
 * Expiry of a vendor API key that the customer creates by hand and that cannot be refreshed (REV-21119 D7).
 *
 * Accounto keys are JWTs with an `exp`; the BE stores it as the connection's `tokenExpiresAt`. Nothing renews
 * such a key, so the user is warned ahead of time on the integration card and in the manage modal:
 *  - `expiring` from {@link API_KEY_EXPIRY_WARNING_DAYS} days before the expiry,
 *  - `expired` from the expiry on (the BE then answers every call with `KEY_EXPIRED`).
 *
 * Only for vendors whose `tokenExpiresAt` is a hand-made key: for OAuth vendors (D365 BC) it is an access
 * token the BE refreshes, and warning about it would be wrong.
 */
export type ApiKeyExpiryState = 'valid' | 'expiring' | 'expired';

export const API_KEY_EXPIRY_WARNING_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `null` when the connection carries no (readable) expiry date. */
export function apiKeyExpiryState(
    tokenExpiresAt: Date | string | null | undefined,
    now: Date = new Date(),
): ApiKeyExpiryState | null {
    if (tokenExpiresAt == null) return null;
    const expiresAt = new Date(tokenExpiresAt).getTime();
    if (Number.isNaN(expiresAt)) return null;
    if (expiresAt <= now.getTime()) return 'expired';
    if (expiresAt - now.getTime() <= API_KEY_EXPIRY_WARNING_DAYS * DAY_MS) return 'expiring';
    return 'valid';
}

/** The most urgent state of several connections (one expired key outweighs any number of valid ones). */
export function worstApiKeyExpiryState(states: (ApiKeyExpiryState | null)[]): ApiKeyExpiryState | null {
    if (states.includes('expired')) return 'expired';
    if (states.includes('expiring')) return 'expiring';
    if (states.includes('valid')) return 'valid';
    return null;
}
